import { CREDIT_SCALE, formatCredits } from './credits'

const intlLocale = (locale) =>
  locale === 'ms' ? 'ms-MY' : locale === 'ar' ? 'ar-EG' : locale === 'en' ? 'en-US' : 'id-ID'

/**
 * money memformat nilai mata uang, dan TIDAK PERNAH melempar.
 *
 * `Intl.NumberFormat` melempar `RangeError` untuk kode yang bukan ISO 4217.
 * Sejak ada pesanan berdenominasi kredit di database, `currency` bisa berisi
 * `"CREDIT"` — dan satu pesanan seperti itu MEMATIKAN SELURUH HALAMAN riwayat
 * transaksi, bukan hanya barisnya. Ditemukan lewat uji peramban di /me/orders.
 *
 * Pemformat angka tidak pantas jadi penyebab halaman kosong. Kode yang tidak
 * dikenal jatuh ke angka polos berlabel, yang tetap bisa dibaca orang.
 */
export const money = (n, currency = 'IDR', locale = 'id') => {
  const value = Number(n) || 0
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(value)
  } catch {
    return `${new Intl.NumberFormat(intlLocale(locale)).format(value)} ${currency}`
  }
}

export const compact = (n) =>
  new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0)

export const num = (n) => new Intl.NumberFormat('id-ID').format(n || 0)

/**
 * Rupiah ringkas untuk label grafik batang.
 *
 * Grafik GMV dulu memakai satuan juta yang dipatok mati ("156jt"), jadi katalog
 * yang penjualannya masih ratusan ribu akan tampil "0jt" di seluruh batang —
 * terbaca seperti tidak ada penjualan sama sekali. Satuannya sekarang mengikuti
 * besar angkanya.
 */
export const rupiahShort = (n) => {
  const v = +n || 0
  if (Math.abs(v) >= 1e9) return `${(v / 1e9).toFixed(1).replace('.', ',')} M`
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toFixed(1).replace('.', ',')} jt`
  if (Math.abs(v) >= 1e3) return `${Math.round(v / 1e3)} rb`
  return num(v)
}

/**
 * Kredit ringkas untuk label batang grafik.
 *
 * `rupiahShort` salah dua kali untuk kredit, dan keduanya tidak kelihatan salah:
 * satuannya rupiah ("156jt") dan angkanya seratus kali terlalu besar, karena
 * kredit dikirim dalam 1/100 kredit. Label grafik adalah tempat terakhir orang
 * akan curiga pada satuan, jadi helper-nya dipisah alih-alih `rupiahShort`
 * dibengkokkan — ia masih dipakai untuk nominal rupiah warisan.
 *
 * Di bawah 10.000 kredit nilainya ditulis penuh (dengan dua angka desimal):
 * katalog yang penjualannya masih puluhan kredit akan tampil "0" di seluruh
 * batang kalau langsung diringkas, dan itu terbaca seperti tidak ada penjualan.
 *
 * Tidak ada aritmetika float atas nominal: pembagian dengan `CREDIT_SCALE`
 * dilakukan setelah sisa bagi dibuang, jadi hasilnya bilangan bulat eksak.
 * Angka yang masuk ke `Intl` sesudah itu hanya untuk DITAMPILKAN.
 */
export const creditsShort = (minor, locale = 'id') => {
  const n = Math.trunc(Number(minor) || 0)
  const abs = Math.abs(n)
  if (abs < 10_000 * CREDIT_SCALE) return formatCredits(n, locale)
  const whole = (abs - (abs % CREDIT_SCALE)) / CREDIT_SCALE
  const short = new Intl.NumberFormat(intlLocale(locale), { notation: 'compact', maximumFractionDigits: 1 }).format(whole)
  return n < 0 ? `-${short}` : short
}

export const dur = (sec) => {
  if (!sec) return '—'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}` : `${m}:${String(s).padStart(2, '0')}`
}

export const mins = (m) => (m >= 60 ? `${Math.floor(m / 60)} jam ${m % 60 ? `${m % 60} mnt` : ''}`.trim() : `${m} mnt`)

export const date = (iso, locale = 'id', opts) =>
  iso ? new Date(iso).toLocaleDateString(locale === 'en' ? 'en-GB' : locale === 'ms' ? 'ms-MY' : locale === 'ar' ? 'ar-EG' : 'id-ID', opts || { day: 'numeric', month: 'short', year: 'numeric' }) : '—'

export const dateTime = (iso, locale = 'id') =>
  iso ? new Date(iso).toLocaleString(locale === 'en' ? 'en-GB' : 'id-ID', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

export const relative = (iso) => {
  if (!iso) return '—'
  const diff = (Date.now() - new Date(iso)) / 1000
  const rtf = new Intl.RelativeTimeFormat('id', { numeric: 'auto' })
  const units = [['year', 31536000], ['month', 2592000], ['day', 86400], ['hour', 3600], ['minute', 60]]
  for (const [u, s] of units) if (Math.abs(diff) >= s) return rtf.format(-Math.round(diff / s), u)
  return 'baru saja'
}

export const countdown = (iso) => {
  const ms = new Date(iso) - Date.now()
  if (ms <= 0) return null
  const d = Math.floor(ms / 864e5), h = Math.floor((ms % 864e5) / 36e5), m = Math.floor((ms % 36e5) / 6e4)
  return d ? `${d}h ${h}j` : h ? `${h}j ${m}m` : `${m} menit`
}

export const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase()
